import React from 'react';
import {SvgIcon, SvgIconProps} from '@mui/material';

/**
 * Warsaw Icon component based on the provided Warsaw logo SVG.
 * - In colored mode: red shield, white mermaid, golden crown and tail.
 * - In monochrome: everything uses currentColor.
 */
export default function WarsawIcon({colored = false, ...props}: {
    colored?: boolean;
} & SvgIconProps) {
    const shieldFill = colored ? '#e62314' : 'currentColor';
    const whiteFill = colored ? '#ffffff' : 'currentColor';
    const goldFill = colored ? '#ffc837' : 'currentColor';

    return (
        <SvgIcon {...props} viewBox={colored ? "0 315 720 950" : "50 360 620 800"}>
            {/* Background Shield */}
            {colored && (
                <path
                    d="M360,1260.2c18.6-52.4,68.6-90,127.4-90h142.6c49.6,0,90-40.2,90-90V360.2H0v720c0,49.8,40.2,90,90,90H232.6c58.8,0,108.8,37.6,127.4,90h0Z"
                    fill={shieldFill}
                />
            )}

            {/* Mermaid Body (White parts) */}
            <path
                d="M393.6,649c.4-1,1-2,1.4-3,5.6-10.8,13-20.4,21.6-28.8-11.4-4.8-57.8-24.2-63.8-26.8-14.2-6.2-4.2-36.2-2.8-46.2-31.6-4.6-32-55-48.6-61.6h0c-7.4,4-9.6,9.8-11,19.4-.6,4.4,1,5.2-2.6,9.6-1.8,2.2-9,6.6-9.2,8.6-.6,3.6,5.8,5.2,7,5.4,0,1.6-1.6,4-1.6,7s1.6,4.2,1.4,6c-.4,3.2-3,6.6-1,9.2,1,1.4,3.6,2.6,6.8,3.4,9.2,2,19.8,0,28.8-1.8,.2,0,.4,0,.6,.4-1.6,2.4-4.6,4-7,5.6-1.8,1-3.6,1.8-5.4,2.6,1,7.8-2,25.2-5.2,24.8-35-4.4-49.4-20.2-101.6-36.6,2.6-25.8,14.2-72.8,18-87.2-3.2-1-6.4-2.2-9-4h.2c4,1,9.2,.2,11.6,0,4.4-.6,12.4-2.4,14.2-4,.8-.6,2.8-5.6,4.4-11.4l12.2-.2v4.8l2.4,19.2h13.8v-20.8c3.6-.2,42-1.8,91-1.8,118,0,164.8,32.4,193,62.2v-7.4c-18.8-39.4-76-75.2-193-75.2h-80.4c-4,6.8-8.4,16.4-10.8,21.4v-42.2h-13.8l-2.4,18.4v5.6l-10-.2c.2-2.4,.2-4.2,.2-4.8-3-1.2-6.6,1.8-7.4,4.4-.6,1.8-5.2,16.2-5.2,16.2h-12.2c.6-.4,1-1,1.2-1.8l.4-1.4c.2,.2,.4,.4,.6,.4,1.8,.6,3.8-.4,4.4-2.2,0,0,2.4-7.2,3-9,.6-2,2-2,3.2-2h4.4c0-3-5.2-8.6-5.2-8.6-1.8-1.8-4.6-1.8-6.2,0-.2,.2-.4,.4-.4,.8-1-1.2-2-2.2-2-2.2-1.8-1.8-4.6-1.8-6.4,0-.4,.4-.8,1-1,1.4-.8-1-2-1.8-3.4-1.8-2.4,0-4.4,2-4.4,4.4v.2c-.6-.4-1.4-.6-2.2-.6-2.2,.2-4,2-3.8,4.4l.2,1.6-20.6-.4v18.4l14.8-.2c-.4,2.2-.6,5.2-.4,6.6,.4,2.4,1.6,3.6,2.2,5,.4,16.4-35,91.2-28,108.8,7.8,19.4,92.2,43,85.4,60-6,15.2-17,26.6-15,34.4,1.8,7.6,6,13.8,11.8,16.8h0c3,1.8,6.2,3,9.2,3.8,.4,.2,.4-.2,.6,.6,0,.6-.2,.4-1,.8-3,.6-6,.8-9,.4v46.4h-.2c-19.8,19.8-36,41.2-48,65,38.8,38,98.6,50,150.6,26.2-1-27.4-7-46.2-17.2-71.4,0,0,41-85.8,46-91,3.6-4.6,8.2-4.8,11.8-4m-98,31.4l1.2-.8c5,7,13,11.6,22.2,11.6,13,0,23.8-9.2,26.6-21.4,.6,0,1.6,.2,1.6,.2,5.4,32.8-33.6,49.4-51.6,10.4"
                fill={whiteFill}
            />

            {/* Mermaid Tail and other details (Gold) */}
            <path
                d="M383.2,520c3.8,4.2,11.4,5,21,2.6h0c-5.2,8.2-12.8,13.6-21,17.2-12.2,4.2-23.4,5-33.4,4.2-31.6-4.6-32-55-48.6-61.6-4.2-4,4.4-9.4,10.2-11.4,13.4-4.8,30.2-2,44.8,6.6,18.4,11,22,25,56.4,23-.2,13.4-22,20.8-29.4,19.4"
                fill={goldFill}
            />
            <path
                d="M419.2,614.6c-9.8,8.8-18,19.4-24,31.4-8.2,15.4-12.6,33.2-12.6,51.8,0,62.2,50.4,112.6,112.4,112.6s112.6-50.4,112.6-112.6-50.4-112.4-112.6-112.4c-29.2-.2-55.8,11-75.8,29.2"
                fill={goldFill}
            />
            <path
                d="M459.4,897c-25.8-16.2-60-8-76.6,18.4,35.2,0,63.8,28.6,63.8,63.8s-28.6,63.8-63.8,63.8-63.8-28.6-63.8-63.8c0-57.8,34-104.2,34-156.6,0-2.8-.2-5.4-.2-7.8-52,23.8-111.8,11.8-150.6-26.2-14.4,28.6-22.4,60.6-22.4,96.8h0c0,138.6,80.8,225,180.2,225h0c74.6,0,139-55.2,139-138,0-70.2,11.2-95.6,29.2-113.4-30-8.6-60.8,8.6-68.8,38"
                fill={goldFill}
            />
        </SvgIcon>
    );
}
